import { Component, computed, OnInit, signal, Signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject, merge } from 'rxjs';
import { finalize, switchMap, take } from 'rxjs/operators';
import { startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { AppValidators } from 'src/app/common/appValidators';
import { AuditLogEventsTableColumns } from 'src/app/models/tableColumns/auditLogEventsTableColumns.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { JsonHalCollection, JsonHalCollectionPageInfo } from 'src/app/models/api/jsonhal.model';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { AuditLogEventDetailsDialogComponent } from 'src/app/components/audit-log-event-details-dialog/audit-log-event-details-dialog.component';
import { AuditLogEventApiResponse, AuditLogEventRowData } from 'src/app/models/api/accountManagement.model';
import { AuditLogIconService } from 'src/app/shared/services/audit-log-icon.service';
import { AuditLogEventsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/AuditLogEventsFilters.model';
import { AuditLogObjectTypeOptions } from 'src/app/models/selectOptions/auditLogObjectTypeOptions.model';
import { AuditLogActionOptions } from 'src/app/models/selectOptions/auditLogActionOptions.model';
import { ISelectOption } from 'src/app/models/selectOptions/selectOption.interface';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { humanize } from 'src/app/common/stringUtils';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';

@Component({
  selector: 'app-audit-log-events',
  templateUrl: './audit-log-events.component.html',
  styleUrls: ['./audit-log-events.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogEventsComponent implements OnInit {
  public columns = signal<ITableColumn[]>(AuditLogEventsTableColumns);
  public pageName = TableColumnsNames.AuditLogEvents;
  public auditLogEventsCurrentPage$ = new BehaviorSubject<AuditLogEventRowData[]>([]);
  public isLoading = signal<boolean>(true);
  public showNoData = signal<boolean>(false);
  public readonly defaultPageSize = signal<number>(this._accountService.defaultPageSize);
  public pageInfo = signal<ITablePageInfo>({
    number: 1,
    currSize: 0,
    maxSize: this.defaultPageSize(),
    totalItems: 0,
    totalPages: 1,
  });
  public sortedColumn = signal<SortColumn | undefined>(undefined);
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
  public isEmptyTable: Signal<boolean>;
  public isFiltersMenuOpen = signal<boolean>(false);

  private _refreshData$: Subject<void> = new Subject<void>();
  private _pageChange$: Subject<number> = new Subject<number>();
  private _pageOffset = signal<number>(0);
  private _previousOffsets: number[] = [];
  private _actionFilter$ = new BehaviorSubject<string | undefined>(undefined);
  private _currentActionFilter: string | undefined = undefined;
  public filtersModel = new AuditLogEventsFiltersModel(AuditLogObjectTypeOptions, AuditLogActionOptions);
  public selectedFilters$ = this.filtersModel.onUpdate$;
  public pageFilters = this.filtersModel;
  public searchBoxNewValue = new BehaviorSubject<string | undefined>(undefined);
  public searchValidators = [AppValidators.evenNumberOfQuoteMarks];
  public fromDateNewValue = new BehaviorSubject<Date | undefined>(undefined);
  public toDateNewValue = new BehaviorSubject<Date | undefined>(undefined);

  constructor(
    private readonly _accountService: AccountManagementHttpService,
    private readonly _logger: LoggerService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _destroyRef: DestroyRef,
    private readonly _dialog: MatDialog,
    private readonly _auditLogIconService: AuditLogIconService
  ) {
    this.isEmptyTable = computed(() => {
      return this.showNoData() && !this.isLoading();
    });
  }

  ngOnInit(): void {
    if (!this.sortedColumn()) {
      const tsCol = (AuditLogEventsTableColumns || []).find((c) => c.propertyKey === 'timestamp');
      if (tsCol) {
        const defaultSort: SortColumn = { column: tsCol, isDesc: true };
        this.sortedColumn.set(defaultSort);
        this.sortedColumn$.next(defaultSort);
      }
    }

    merge(this.sortedColumn$, this._refreshData$, this._pageChange$, this._actionFilter$, this.selectedFilters$)
      .pipe(
        switchMap((value) => {
          this.auditLogEventsCurrentPage$.next([]);
          this.isLoading.set(true);
          let currentSort: SortColumn | undefined;
          const maybeSort = value as SortColumn | undefined;
          if (maybeSort && typeof maybeSort === 'object' && (maybeSort as SortColumn).column !== undefined) {
            currentSort = maybeSort as SortColumn;
            this.sortedColumn.set(currentSort);
            this._pageOffset.set(0);
            this._previousOffsets = [];
          } else if (typeof value === 'number') {
            this._pageOffset.set(value);
            currentSort = this.sortedColumn();
          } else {
            currentSort = this.sortedColumn();
            this._pageOffset.set(0);
            this._previousOffsets = [];
          }

          const selected = this.filtersModel.getSelectedFilters();
          const normalize = (s: string) => s.trim().replace(/\s+/g, '_').toLowerCase();
          const selectedActions = this._currentActionFilter
            ? [this._currentActionFilter]
            : (selected.executionStatus && selected.executionStatus.length > 0) || selected.exempted
              ? [...((selected.executionStatus ?? []) as string[]), ...(selected.exempted ? ['exempted'] : [])]
              : [];
          const normalizedActions = (selectedActions || [])
            .filter((a): a is string => !!a && a.length > 0)
            .map((a) => normalize(a));
          const actionParam = normalizedActions.length > 0 ? normalizedActions.join(',') : undefined;
          const objectTypeParam =
            selected.type && selected.type.length > 0 ? selected.type.map((t) => normalize(t)).join(',') : undefined;
          const searchParam = selected.search;
          const selectedTyped = selected as ISelectedFilters;
          const user = selected.user && selected.user.length > 0 ? selected.user.join(',') : undefined;
          const actorTypeParam =
            selectedTyped.actorType && selectedTyped.actorType.length > 0
              ? selectedTyped.actorType.join(',')
              : undefined;

          const startTime = selected.fromDate
            ? formatInTimeZone(
                startOfDay(this._toDate(selected.fromDate as unknown)),
                'UTC',
                "yyyy-MM-dd'T'HH:mm:ss'Z'"
              )
            : undefined;
          const endTime = selected.toDate
            ? formatInTimeZone(endOfDay(this._toDate(selected.toDate as unknown)), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'")
            : undefined;

          const objectId = this.filtersModel.objectIdentifier.getFilterValue();

          return this._accountService
            .getAuditLogEvents({
              entityActions: actionParam,
              sortData: currentSort,
              pageSize: this.defaultPageSize(),
              pageOffset: this._pageOffset(),
              search: searchParam,
              user,
              startTime,
              endTime,
              objectTypeCsv: objectTypeParam,
              actorSources: actorTypeParam,
              objectIdentifier: objectId,
            })
            .pipe(finalize(() => this.isLoading.set(false)));
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this._handleAuditLogEventsResponse(response),
        error: (error) => this._fetchRequestOnError(error),
      });

    this._refreshData$.next();
  }

  private async _handleAuditLogEventsResponse(response: JsonHalCollection<AuditLogEventApiResponse>) {
    this._handlePageInfo(response.page);

    const items = response._embedded.items;

    if (!items || items.length == 0) {
      this.showNoData.set(true);
      this.auditLogEventsCurrentPage$.next([]);
    } else {
      this.showNoData.set(false);
      const eventViewModelTemp = items.map((item) => this._transformEventToViewModel(item));
      this.auditLogEventsCurrentPage$.next(eventViewModelTemp);
    }
  }

  private _transformEventToViewModel(event: AuditLogEventApiResponse): AuditLogEventRowData {
    return {
      id: event.id,
      rowId: event.id,
      timestamp: new Date(event.timestamp),
      objectType: humanize(event?.entityType || ''),
      objectId: event?.metadata?.entity_id || '',
      action: humanize((event?.entityAction || '').replace(/_/g, ' ')),
      actorType: event?.actor?.source || '',
      actorEmail: event?.actor?.email ? [event?.actor?.email] : [],
      details: event?.metadata && Object.keys(event.metadata).length > 0 ? 'Read more' : '',
      clickable: false,
      iconSrc: this._auditLogIconService.getObjectTypeIcon(event?.entityType, true),
      objectTypeIconClass: 'object-type-icon',
      fullEvent: event,
    };
  }

  private _toDate(v: unknown): Date {
    if (v && typeof (v as Date).getTime === 'function') return v as Date;
    if (v && typeof (v as { toJSDate?: () => Date }).toJSDate === 'function') {
      return (v as { toJSDate: () => Date }).toJSDate();
    }
    if (v && typeof (v as { toUTC?: () => { year?: number; month?: number; day?: number } }).toUTC === 'function') {
      const now = new Date();
      const u = (v as { toUTC: () => { year?: number; month?: number; day?: number } }).toUTC();
      const y = u?.year ?? now.getUTCFullYear();
      const m = (u?.month ?? now.getUTCMonth() + 1) - 1; // zero-based month
      const d = u?.day ?? now.getUTCDate();
      return new Date(Date.UTC(y, m, d, 0, 0, 0));
    }
    return new Date();
  }

  private _fetchRequestOnError(error: HttpErrorResponse): void {
    this.auditLogEventsCurrentPage$.next([]);
    this.isLoading.set(false);
    this.showNoData.set(true);
    this._httpErrorHandlerService.handle(error);
    this._logger.error('Error loading audit log events:', error);
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize());
    this.pageInfo.set(tablePageInfo);
  }

  public onPageChange(operation: PaginationOperationEnum): void {
    if (this.isLoading()) return;

    const info = this.pageInfo();
    const pageSize = info.maxSize;

    if (!info.totalItems || !info.currSize) return;

    if (operation === PaginationOperationEnum.next) {
      if (this._pageOffset() + pageSize >= info.totalItems) return;
      this._previousOffsets.push(this._pageOffset());
      const newOffset = this._pageOffset() + pageSize;
      this._pageChange$.next(newOffset);
    } else if (operation === PaginationOperationEnum.prev) {
      if (this._previousOffsets.length > 0) {
        const prevOffset = this._previousOffsets.pop()!;
        this._pageChange$.next(prevOffset);
      } else {
        if (this._pageOffset() <= 0) return;
        const newOffset = Math.max(0, this._pageOffset() - pageSize);
        this._pageChange$.next(newOffset);
      }
    }
  }

  public onActionFilterChange(action: string | null): void {
    this._currentActionFilter = action && action.length ? action : undefined;
    this._actionFilter$.next(this._currentActionFilter);
  }

  public updateFiltersFromOptions(options: ISelectOption[]): void {
    this.filtersModel.updateFilterFromUserSelection(undefined, options);
    // Ensure the user filter exactly matches the current selection (including empty)
    const userOptions = options.filter((o) => o.filterType === (FilterTypesEnum.User as unknown as FilterTypesEnum));
    this.filtersModel.updateFilterFromUserSelection(FilterTypesEnum.User as unknown as FilterTypesEnum, userOptions);
  }

  public tablesFiltersChange(options: ISelectOption[]): void {
    this.updateFiltersFromOptions(options);
  }

  public filtersOnMenuStateChange(open: boolean): void {
    this.isFiltersMenuOpen.set(open);
  }

  public onExportClick(): void {
    const selected = this.filtersModel.getSelectedFilters();
    const sort = this.sortedColumn();

    const normalize = (s: string) => s.trim().replace(/\s+/g, '_').toLowerCase();
    const toCsv = (arr?: string[]) =>
      (arr ?? [])
        .map((s) => s?.trim())
        .filter((s): s is string => !!s)
        .map(normalize)
        .join(',') || undefined;

    const selectedActions = this._currentActionFilter
      ? [this._currentActionFilter]
      : [...((selected.executionStatus ?? []) as string[]), ...(selected.exempted ? ['exempted'] : [])];

    const actionParam = toCsv(selectedActions);
    const objectTypeParam = toCsv(selected.type);

    const startTime = selected.fromDate
      ? formatInTimeZone(startOfDay(this._toDate(selected.fromDate as unknown)), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'")
      : undefined;
    const endTime = selected.toDate
      ? formatInTimeZone(endOfDay(this._toDate(selected.toDate as unknown)), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'")
      : undefined;

    this.isLoading.set(true);

    this._accountService
      .exportAuditLogEvents({
        entityActions: actionParam,
        search: selected.search,
        user: (selected.user && selected.user.length > 0 ? selected.user.join(',') : undefined) ?? undefined,
        startTime,
        endTime,
        objectTypeCsv: objectTypeParam,
        objectIdentifier: this.filtersModel.objectIdentifier.getFilterValue(),
        sortData: sort,
      })
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          const url = res?.fileUrl;
          if (!url) return;

          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          a.remove();
        },
        error: (error) => {
          this.isLoading.set(false);
          this._httpErrorHandlerService.handle(error);
          this._logger.error('Error exporting audit log events:', error);
        },
      });
  }

  public onFilterChange(): void {
    this._refreshData$.next();
  }

  public searchSubmitted(value?: string): void {
    this.filtersModel.updateFilterFromUserSelection(FilterTypesEnum.Search as unknown as FilterTypesEnum, value);
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  public onDetailsClick(row: AuditLogEventRowData): void {
    if (!row || row.details !== 'Read more') {
      return;
    }

    const evt = row?.fullEvent;
    if (evt) {
      this.openEventDetailsModal(evt);
    }
  }

  public openEventDetailsModal(event: AuditLogEventApiResponse): void {
    this._dialog.open(AuditLogEventDetailsDialogComponent, {
      width: '600px',
      maxHeight: '100vh',
      data: event,
    });
  }
}
